import * as React from 'react';
import { Tag, Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@components/atoms';
import type { TAllowedColors } from '@components/atoms/tag/types';
import { cn } from '@utils';

export interface TagItem {
	color?: TAllowedColors;
	value: string;
}

export interface StatusCellProps {
	iconsLeft?: React.ReactNode[];
	iconsRight?: React.ReactNode[];
	dataTestId?: string;

	// Tags section
	tags?: TagItem[];

	// CTA section
	ctaIcon?: React.ReactNode;
	ctaTooltip?: React.ReactNode;

	// More content (second row)
	moreContent?: string;
	moreContentHover?: React.ReactNode;
	moreContentLink?: string;
}

/**
 * A flexible status cell component for displaying complex status information with icons, tags, and CTAs
 *
 * @component
 * @example
 * // Basic usage with a single tag
 * <StatusCell
 *   tags={[{ value: 'Active', color: 'green' }]}
 * />
 *
 * @example
 * // With icons and multiple tags
 * <StatusCell
 *   iconsLeft={[<UserIcon />, <StarIcon />]}
 *   tags={[
 *     { value: 'Premium', color: 'purple' },
 *     { value: 'Active', color: 'green' }
 *   ]}
 * />
 *
 * @example
 * // With CTA and more content
 * <StatusCell
 *   tags={[{ value: 'Pending', color: 'orange' }]}
 *   ctaIcon={<InfoIcon />}
 *   ctaTooltip="Click for more information"
 *   moreContent="View details..."
 *   moreContentLink="/details/123"
 * />
 */
export const StatusCell: React.FC<StatusCellProps> = ({
	iconsLeft = [],
	iconsRight = [],
	dataTestId,
	tags = [],
	ctaIcon,
	ctaTooltip,
	moreContent,
	moreContentHover,
	moreContentLink,
}) => {
	const contentRef = React.useRef<HTMLDivElement>(null);

	/**
	 * Renders the more content with optional tooltip.
	 * @returns {JSX.Element | null} The rendered more content or null if not provided.
	 */
	const renderMoreContent = () => {
		if (!moreContent) return null;

		const content = (
			<div
				ref={contentRef}
				className={cn(
					'text-xs text-secondary underline truncate',
					moreContentLink && 'cursor-pointer hover:text-primary'
				)}
			>
				{moreContent}
			</div>
		);

		if (moreContentHover) {
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						{moreContentLink ? (
							<a href={moreContentLink} className="block">
								{content}
							</a>
						) : (
							content
						)}
					</TooltipTrigger>
					<TooltipContent>{moreContentHover}</TooltipContent>
				</Tooltip>
			);
		}

		return moreContentLink ? (
			<a href={moreContentLink} className="block">
				{content}
			</a>
		) : (
			content
		);
	};

	return (
		<TooltipProvider delayDuration={0} skipDelayDuration={0}>
			<div data-aid="data-grid-status-cell" data-testid={dataTestId} className="flex min-w-0 flex-col">
				<div className="flex items-center gap-1.5">
					{/* Left Icons section */}
					{iconsLeft.length > 0 && (
						<div className="flex flex-wrap gap-1">
							{iconsLeft.map((icon, index) => (
								<React.Fragment key={`icon-left-${index}`}>
									{icon}
								</React.Fragment>
							))}
						</div>
					)}

					{/* Tags section */}
					{tags.length > 0 && (
						<div className="flex flex-wrap gap-1">
							{tags.map((tag, index) => (
								<Tag key={`tag-${index}`} color={tag.color || 'green'}>
									{tag.value}
								</Tag>
							))}
						</div>
					)}

					{/* Right Icons section */}
					{iconsRight.length > 0 && (
						<div className="ml-0 flex items-center gap-1">
							{iconsRight.map((icon, index) => (
								<div key={`icon-right-${index}`} className="shrink-0">
									{icon}
								</div>
							))}
						</div>
					)}

					{/* CTA section - keep for backward compatibility */}
					{ctaIcon && (
						<div className="ml-0 shrink-0">
							{ctaTooltip ? (
								<Tooltip>
									<TooltipTrigger asChild>
										<div className="cursor-pointer">{ctaIcon}</div>
									</TooltipTrigger>
									<TooltipContent>{ctaTooltip}</TooltipContent>
								</Tooltip>
							) : (
								ctaIcon
							)}
						</div>
					)}
				</div>

				{/* More content (second row) */}
				{renderMoreContent()}
			</div>
		</TooltipProvider>
	);
};
